// Globals
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Modules
import { Header } from '@/modules/Layout';
import { getConversationStatusLabel } from '@/modules/Conversations/helpers';

// Components
import { BackLink } from '@/components';
import { Spinner } from '@/ui';
import {
  StyledColumn,
  StyledContainer,
  StyledLoaderWrapper,
  StyledSubtitle,
  StyledTitlePill,
} from './ConversationDetailsContainerStyles';
import { ConversationSuggestedFaqCard, ConversationSummaryCard, ConversationTranscriptCard } from '../../components';

// Helpers
import { ROUTES } from '@/constants/routes';
import { getShortId } from '@/utils';
import { useGoBack } from '@/hooks';
import { useGetConversationById, useGetConversationChannelTheme } from '../../hooks';
import { getConversationPeriodLabel } from '../../helpers';

const ConversationDetailsContainer = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { conversation, isLoading } = useGetConversationById(id);
  const { theme, isLoading: isThemeLoading } = useGetConversationChannelTheme(conversation?.channelSlug);
  const handleBackClick = useGoBack();

  if (isLoading && !conversation) {
    return (
      <StyledLoaderWrapper>
        <Spinner />
      </StyledLoaderWrapper>
    );
  }

  return (
    <>
      <Header
        suptitle={
          <BackLink to={ROUTES.CONVERSATIONS} onClick={handleBackClick}>
            {t('back_to_conversations')}
          </BackLink>
        }
        title={
          <>
            {getShortId(conversation?.conversationId)}
            {conversation && (
              <StyledTitlePill $status={conversation.state}>
                {getConversationStatusLabel(conversation.state, t)}
              </StyledTitlePill>
            )}
          </>
        }
        description={
          conversation && (
            <StyledSubtitle>
              {conversation.channelSlug} &middot; {getConversationPeriodLabel(conversation)}
            </StyledSubtitle>
          )
        }
      />
      <StyledContainer>
        <StyledColumn>
          <ConversationTranscriptCard
            messages={conversation?.messages ?? []}
            userMessageTheme={theme}
            isLoading={isThemeLoading}
          />
        </StyledColumn>
        <StyledColumn>
          <ConversationSummaryCard summary={conversation?.summary} />
          {!!conversation?.suggestions?.length && (
            <ConversationSuggestedFaqCard suggestions={conversation.suggestions} />
          )}
        </StyledColumn>
      </StyledContainer>
    </>
  );
};

export default ConversationDetailsContainer;
